import { useMutation } from '@apollo/client';
import { useI18n } from '@i18n';
import { PortfolioManagementPortfolioFragment } from '@predium/client-graphql';
import { emission_factor_type_enum } from '@predium/enums';
import { useSnackbar } from 'notistack';
import { useSession } from '@/components/session/useSession';
import { SnackbarTimeouts } from '../../../components/NotistackProvider';
import { GET_ORG_PREFERENCES } from '../../../graphql/common.queries';
import { CREATE_PORTFOLIO } from '../../../graphql/Portfolio.mutations';
import { GET_PORTFOLIOS, GET_PORTFOLIOS_WITH_WRITE_PERMISSIONS } from '../../../graphql/Portfolio.queries';
import useOrgPreferences from '../../../hooks/useOrgPreferences';
import { CrremGuard } from '../Settings/util';
import { CrremCreatePortfolioForm } from './CrremCreatePortfolioForm';
import { NationalLawPortfolioForm } from './NationalLawCreatePortfolioForm';

type Props = {
  open: boolean;
  onClose: () => void;
  onPortfolioCreate?: () => void;
  portfolios: PortfolioManagementPortfolioFragment[];
};

export type PortfolioFormValuesProps = {
  name: string;
  crremConfigId?: string;
};

export default function PortfolioCreateDialog({ open, onClose, onPortfolioCreate, portfolios }: Props) {
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();

  const {
    orgPreferences: { emissionFactorType },
  } = useOrgPreferences();

  const {
    user: { id: userId },
  } = useSession();

  const [createMutateFunction, { loading }] = useMutation(CREATE_PORTFOLIO, {
    onError: () => {
      enqueueSnackbar(t('PortfolioCreateDialog_SnackbarResponse-Error'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });
    },
    onCompleted: () => {
      enqueueSnackbar(t('PortfolioCreateDialog_SnackbarResponse-Success'), {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      });
      onClose();
      onPortfolioCreate?.();
    },
    refetchQueries: [
      GET_PORTFOLIOS,
      { query: GET_PORTFOLIOS_WITH_WRITE_PERMISSIONS, variables: { user_id: userId } },
      GET_ORG_PREFERENCES,
    ],
  });

  const commonProps = {
    open,
    onClose,
    onPortfolioCreate,
    portfolios,
    loading,
    createMutateFunction,
  };

  if (emissionFactorType === emission_factor_type_enum.NATIONAL_ENERGY_LAW) {
    return <NationalLawPortfolioForm {...commonProps} />;
  }

  return (
    <CrremGuard>
      {(orgPreferences) => {
        return <CrremCreatePortfolioForm {...commonProps} orgPreferences={orgPreferences} />;
      }}
    </CrremGuard>
  );
}
